/**
 * The single gate every copilot entry point passes through.
 *
 * Available only when a provider is mounted above the caller, the role has an
 * entry point, and the deployment serves the feature. Requiring the provider
 * is what makes the shared Navbar safe on jobseeker pages, and what makes
 * removing the provider switch the copilot off cleanly everywhere.
 */

import { useSelector } from "react-redux";

import { getUserRole, normalizeRoleId } from "../constants/roleConfig";
import { absoluteCopilotUrl } from "./api";
import {
  COPILOT_ALLOWED_ROLES,
  COPILOT_PROTOCOL_VERSION,
  COPILOT_REST,
} from "./constants";
import { useCopilot } from "./CopilotProvider";
import { useCopilotStatus } from "./useCopilotStatus";

/**
 * Dev escape hatch for building UI before the endpoints exist on an
 * environment: `localStorage.setItem("copilot:force", "1")`, then reload.
 * It bypasses the status probe only — never the role gate or the provider.
 */
export const FORCE_KEY = "copilot:force";

export const isCopilotForced = () => {
  if (!import.meta.env.DEV) return false;
  try {
    return window.localStorage.getItem(FORCE_KEY) === "1";
  } catch {
    return false;
  }
};

/** Why the copilot is unavailable — for the dev console only. */
function hiddenReason({ hasProvider, roleHidden, role, loading, error, status }) {
  if (!hasProvider) return "no CopilotProvider is mounted above this entry point";
  if (roleHidden) {
    return role === undefined || role === null
      ? "the user's role is not known yet, denied until it is"
      : `role ${role} has no copilot entry point`;
  }
  if (loading) return "still probing GET /copilot/status";
  if (error) {
    // The HTTP status is the whole diagnosis: 404 means the endpoints are not
    // deployed here, no status at all means the request never completed.
    const where = absoluteCopilotUrl(COPILOT_REST.status);
    const how = error.status
      ? `HTTP ${error.status}`
      : `no response (${error.code || "network"})`;
    return `GET ${where} failed, ${how}`;
  }
  if (!status) return "GET /copilot/status returned nothing";
  if (!status.configured) return "the deployment reports configured: false";
  if (!status.enabled) return "the deployment reports enabled: false";
  if (status.protocol_version !== COPILOT_PROTOCOL_VERSION) {
    return `protocol_version ${status.protocol_version} does not match the ${COPILOT_PROTOCOL_VERSION} this client speaks`;
  }
  return "unknown";
}

/**
 * @returns {{ available: boolean, forced: boolean, loading: boolean,
 *             reason: string|null }}
 * `forced` is true when the entry point shows only because of FORCE_KEY.
 */
export function useCopilotAvailability() {
  const copilot = useCopilot();
  // The app's own reader (user.role, then user.user_role), falling back to the
  // root-level auth.role the app also uses.
  const role = useSelector(
    (state) => getUserRole(state.auth?.user) ?? normalizeRoleId(state.auth?.role)
  );

  const hasProvider = Boolean(copilot);
  // Allow-list, so an unknown or still-loading role is denied. CLIENT (4) and
  // JOBSEEKER (2) are never allowed.
  const roleHidden = !COPILOT_ALLOWED_ROLES.has(role);
  const skip = !hasProvider || roleHidden;

  const { status, loading, error, available: statusOk } = useCopilotStatus({
    skip,
  });

  const forced = !statusOk && isCopilotForced();
  const available = hasProvider && !roleHidden && (statusOk || forced);
  const probing = !skip && loading;

  return {
    available,
    forced: available && forced,
    loading: probing,
    reason: statusOk && available
      ? null
      : hiddenReason({ hasProvider, roleHidden, role, loading: probing, error, status }),
  };
}
